<template>
  <div class="media-library">
    <AkHeading3 class="ds-pb-3">
      {{ t('brand.account.mediaLibrary.title') }}
    </AkHeading3>
    <div class="ds-relative">
      <AkParagraph
        class="ds-pb-3"
        :class="{ 'ds-opacity-50': isLoading }"
      >
        {{ t('brand.account.mediaLibrary.itemsCount', { count: totalMediaCount }) }}
      </AkParagraph>
      <LoaderIcon
        v-if="isLoading"
        small
        class="ds-absolute ds-top-1 ds-left-1"
      />
    </div>
    <slot name="header">
      <MediaLibraryBanner />
    </slot>

    <span class="separator" />

    <MediaDragNDrop
      class="ds-mb-5"
      :disabled="isUploading"
      @change="emit('mediaChange', $event)"
    />

    <DebouncedSearch
      class="ds-mb-5"
      :placeholder="t('brand.account.mediaLibrary.search.placeholder')"
      :debounce="450"
      @input="onSearchUpdate($event)"
      @cleared="onSearchUpdate('')"
    />

    <MediaUploadAlert />
    <div class="ds-relative">
      <div :class="{ 'ds-opacity-50 ds-pointer-events-none': isLoading }">
        <MediaGrid
          :items="items"
          :is-selectable="mode === 'select'"
          @item-click="emit('itemClick', $event)"
        />
        <MediaLibraryPagination
          v-if="pagination"
          :pagination="pagination"
          @change="handleChangePage($event)"
        />
      </div>
      <LoaderIcon
        v-if="isLoading"
        class="ds-absolute ds-top-1 ds-left-1"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import MediaGrid from './media-grid.vue';
import MediaDragNDrop from './media-drag-n-drop.vue';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import MediaLibraryPagination from './media-library-pagination.vue';
import MediaUploadAlert from './media-upload-alert.vue';
import { AkParagraph, AkHeading3 } from '@ankorstore/design-system';
import type { Media, MediaResponse, MediaLibraryMode } from '../../types';
import { DebouncedSearch } from '@bc/shared';
import LoaderIcon from '@monolith/legacy/components/loader-icon.vue';
import MediaLibraryBanner from './media-library-banner.vue';

withDefaults(
  defineProps<{
    items: Media[];
    isLoading?: boolean;
    isUploading?: boolean;
    pagination?: MediaResponse['meta']['page'];
    totalMediaCount: number;
    mode?: MediaLibraryMode;
  }>(),
  {
    isLoading: false,
    isUploading: false,
    pagination: () => ({ perPage: 50, total: 0, from: 0 }),
    mode: 'view',
  }
);

const emit = defineEmits(['pageChange', 'search', 'mediaChange', 'itemClick']);

const onSearchUpdate = (searchQuery: string) => {
  query.value = searchQuery;
  emit('search', searchQuery);
};

const { t } = useI18n();

const query = ref('');

const handleChangePage = (page: number) => {
  emit('pageChange', page, query.value);
};
</script>

<style scoped lang="scss">
.media-library {
  @apply ds-p-6;
}

.separator {
  @apply ds-border-t ds-w-full ds-block ds-border-solid ds-border-neutral-300 ds-mb-5;
}
</style>
